// Email capture — the single client for POST /api/notify.
//
// Every lead-capture surface (quiz gate, footer newsletter, checklist save,
// visit-planner unlock) used to inline its own `fetch('/api/notify', …)` and
// treat *any* outcome as success. Two things were wrong with that:
//
//   1. The path was relative, so the request went to the Pages host
//      (lodestar.place) instead of the Worker (api.lodestar.place). Pages
//      answers POST on a static path with 405, forever.
//   2. The result was discarded, so the UI showed a thank-you for a request
//      that never stored anything.
//
// This module fixes both: it targets the same API base every other call in
// the app uses, and it reports honestly whether the address was accepted.

const API_BASE: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

/** Where the address was captured. Mirrors the `source` column the API stores. */
export type NotifySource = 'quiz_save' | 'newsletter' | 'checklist_save' | 'premium';

export interface NotifyPayload {
  email: string;
  /** ISO-3166 alpha-2, or a pseudo-code such as NEWSLETTER for non-country captures. */
  countryCode: string;
  source: NotifySource;
  context?: Record<string, unknown>;
}

/**
 * Upper bound on how long a capture may keep the UI waiting. A hung request
 * must not trap the visitor in a spinner — it is reported as a failure and
 * the surface offers a retry.
 */
export const NOTIFY_TIMEOUT_MS = 8000;

function timeoutSignal(): AbortSignal | undefined {
  try {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      return AbortSignal.timeout(NOTIFY_TIMEOUT_MS);
    }
  } catch {
    // Older runtime without AbortSignal.timeout — run without a deadline.
  }
  return undefined;
}

/**
 * Submits one captured address.
 *
 * @returns `true` only when the API answered 2xx (204 on success today).
 *          Any other status, a network error or a timeout returns `false` —
 *          callers must not render a success state on `false`.
 */
export async function submitNotify(payload: NotifyPayload): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/notify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      signal: timeoutSignal(),
      body: JSON.stringify({
        ...payload,
        email: payload.email.trim().toLowerCase(),
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
